import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateMain, formatDatePeriod } from '@hero/core';
import type { Billing, BillingStatus } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { ArrowRightIcon, CalendarIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';
import { Price } from '../../../app/Price';
import { BillingTrustStrip } from '../BillingTrustStrip/BillingTrustStrip';

export interface BillingPaySummaryCardProps {
  billing: Billing;
  /**
   * Pay this billing via Moyasar. The callback opens
   * `billing.checkout.url` directly in a new tab — no async backend
   * call, so there is no `isPaying` state to render.
   */
  onPay?: () => void;
  className?: string;
}

/**
 * Sticky right-rail summary on `BillingDetailPage`.
 *
 * Matches the visual language of `BillingOrderSummary` (the checkout
 * sticky card) so the two flows feel like one product: same outer
 * shell, same totals stack, same trust strip, same primary CTA with
 * an RTL-aware arrow. The unique bits — date rows for Period / Issued
 * / Due / Paid — live in their own panel inside the same card.
 *
 * The CTA only renders when the billing is in a payable state; once
 * paid we show a soft "paid in full" note so the slot doesn't sit
 * empty.
 */
export function BillingPaySummaryCard({
  billing,
  onPay,
  className,
}: BillingPaySummaryCardProps) {
  const { t } = useTranslation('m-billing');
  const { amount, status, period, paidAt, checkout } = billing;
  const taxTotal = amount.tax?.total ?? 0;
  const showTax = taxTotal > 0;
  const payable = isPayable(status) && !!checkout?.url;

  return (
    <aside
      className={cn(
        'sticky top-6 flex flex-col gap-5 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm',
        className,
      )}>
      <header className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-gray-900">
          {t('billing_doc.detail.amount_due_title')}
        </h3>
        <p className="text-xs text-gray-500">
          {t('billing_doc.detail.amount_due_description')}
        </p>
      </header>

      {/* Totals stack — mirrors BillingOrderSummary's subtotal / tax /
          divider / total layout. */}
      <div className="flex flex-col gap-4">
        <SummaryLine
          label={t('billing_doc.detail.totals_subtotal')}
          amount={amount.subtotal}
          currency={amount.currency}
          muted
        />
        {showTax && (
          <SummaryLine
            label={t('billing_doc.detail.totals_tax')}
            amount={taxTotal}
            currency={amount.currency}
            muted
          />
        )}
        <div className="mt-1 flex items-baseline justify-between gap-3 rounded-xl bg-gray-50 px-4 py-3">
          <span className="text-sm font-semibold text-gray-900">
            {t('billing_doc.detail.totals_total')}
          </span>
          <Price
            amount={amount.total}
            currency={amount.currency}
            size="xl"
            className="font-bold text-gray-900"
          />
        </div>
      </div>

      {/* Key dates — Period is the one the user needs at-a-glance
          next to the total ("what window am I paying for?"). Issued
          and Due are intentionally not surfaced here — they're
          implementation detail of the receipt, not actionable for
          the user. Paid stays so post-payment we show "when". */}
      {(period || paidAt) && (
        <dl className="flex flex-col gap-2">
          {period && (
            <DateRow
              label={t('billing_doc.detail.field_period')}
              value={formatDatePeriod(period) || '—'}
            />
          )}
          {paidAt && (
            <DateRow
              label={t('billing_doc.detail.field_paid')}
              value={formatDateOrDash(paidAt)}
            />
          )}
        </dl>
      )}

      {/* Action slot — only rendered when both the status is payable
          AND we have a hosted-checkout URL on the Billing. */}
      {payable ? (
        <Button size="lg" className="w-full" onClick={onPay} disabled={!onPay}>
          <span className="inline-flex items-center gap-2">
            {t('billing_doc.detail.pay_now')}
            <ArrowRightIcon className="size-4 rtl:rotate-180" />
          </span>
        </Button>
      ) : status === 'paid' ? (
        <p className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-700">
          {t('billing_doc.detail.paid_note')}
        </p>
      ) : null}

      {payable && (
        <div className="border-t border-gray-100 pt-4">
          <BillingTrustStrip />
        </div>
      )}
    </aside>
  );
}

interface SummaryLineProps {
  label: string;
  amount: number;
  currency: Billing['amount']['currency'];
  muted?: boolean;
}

function SummaryLine({ label, amount, currency, muted }: SummaryLineProps) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span
        className={cn(
          'text-sm',
          muted ? 'text-gray-500' : 'font-medium text-gray-900',
        )}>
        {label}
      </span>
      <Price
        amount={amount}
        currency={currency}
        className={cn(
          'text-sm tabular-nums',
          muted ? 'text-gray-500' : 'font-semibold text-gray-900',
        )}
      />
    </div>
  );
}

function DateRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-3 py-2">
      <dt className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        <CalendarIcon className="size-3.5 text-gray-400" />
        {label}
      </dt>
      <dd className="text-sm font-medium tabular-nums text-gray-900">{value}</dd>
    </div>
  );
}

function isPayable(status: BillingStatus): boolean {
  return (
    status === 'pending' || status === 'partially_paid' || status === 'past_due'
  );
}

function formatDateOrDash(d: Date | string | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return formatDateMain({ date, format: 'date' });
}
